import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"
import { confirmAction } from "../lib/confirm"

export default class extends Controller {
  static targets = ["formPanel", "row", "connectionCard"]
  static values = {
    createUrl: String,
    updateUrlTemplate: String,
    destroyUrlTemplate: String,
    autoSyncUrlTemplate: String,
    configureLink: String
  }

  connect() {
    this._showPendingFlash()
    this._autoOpenConfigure()
  }

  // After a project-level self-connect we land back here with
  // ?configure_link=<id> — auto-open that link's config picker so connecting
  // and mapping feel like one wizard.
  _autoOpenConfigure() {
    const id = this.hasConfigureLinkValue && this.configureLinkValue
    if (!id) return
    const panel = this._findPanel({ mode: "edit", linkId: id })
    if (panel) panel.classList.remove("hidden")
  }

  openCreate(event) {
    const cid = event.currentTarget?.dataset?.connectionId
    if (!cid) return
    const panel = this._findPanel({ mode: "create", connectionId: cid })
    if (panel) panel.classList.remove("hidden")
  }

  openEdit(event) {
    const id = event.currentTarget?.dataset?.linkId
    if (!id) return
    const panel = this._findPanel({ mode: "edit", linkId: id })
    if (panel) panel.classList.remove("hidden")
  }

  closeForm(event) {
    // Backdrop click fires here too; resolve via target chain rather than currentTarget.
    const panel = event.currentTarget?.closest?.("[data-project-integration-links-target='formPanel']") ||
                  this._panelFromEvent(event)
    if (panel) panel.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async submitForm(event) {
    event.preventDefault()
    const form = event.currentTarget
    const url = form.dataset.formUrl
    const method = (form.dataset.formMethod || "post").toUpperCase()

    const fd = new FormData(form)

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken()
        },
        credentials: "same-origin",
        body: fd
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok !== false) {
        this._flashAndReload(data.message || "Saved.", "notice")
      } else {
        showToast(data.message || "Couldn't save. Try again.", "error")
      }
    } catch (e) {
      showToast("Network error — try again.", "error")
    }
  }

  // § 3.2 Tier 4 Step 36 — flip the per-project auto-sync flag for an
  // ERP link without opening the edit modal. Optimistically updates the
  // pill, posts to the dedicated PATCH endpoint (preserves the link's
  // other config keys), and reloads to re-render the row state. On
  // failure, the toast surfaces the reason and the page reload restores
  // the original pill state from the server.
  async toggleAutoSync(event) {
    const button = event.currentTarget
    const id = button?.dataset?.linkId
    if (!id || !this.autoSyncUrlTemplateValue) return
    const desired = button.dataset.current !== "true"
    const url = this.autoSyncUrlTemplateValue.replace("ID", id)

    button.disabled = true
    const originalLabel = button.innerHTML
    button.innerHTML = "Saving…"

    try {
      const fd = new FormData()
      fd.append("project_integration_link[auto_sync_on_approval]", desired ? "true" : "false")
      const res = await fetch(url, {
        method: "PATCH",
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrfToken() },
        credentials: "same-origin",
        body: fd
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok !== false) {
        this._flashAndReload(data.message || "Auto-sync updated.", "notice")
      } else {
        showToast(data.message || "Couldn't update auto-sync. Try again.", "error")
        button.disabled = false
        button.innerHTML = originalLabel
      }
    } catch (e) {
      showToast("Network error — try again.", "error")
      button.disabled = false
      button.innerHTML = originalLabel
    }
  }

  // § 3.18 Tier 2 Step 23 — manual "Pull from Raken now" trigger.
  // Posts to the field-data manual-sync controller for the link;
  // returns immediately on enqueue (the actual pull runs async via
  // Sidekiq, surfaces in the connection's sync log).
  async pullFromProvider(event) {
    const button = event.currentTarget
    const url = button?.dataset?.pullUrl
    const providerName = button?.dataset?.providerName || "the provider"
    if (!url) return

    button.disabled = true
    const originalLabel = button.innerHTML
    button.innerHTML = "Pulling…"

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken()
        },
        credentials: "same-origin"
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.enqueued) {
        showToast(`Pull from ${providerName} queued. Watch the connection's sync log for results.`, "success")
      } else {
        showToast(data.error || `Couldn't pull from ${providerName}. Try again.`, "error")
      }
    } catch (e) {
      showToast("Network error — try again.", "error")
    } finally {
      button.disabled = false
      button.innerHTML = originalLabel
    }
  }

  async confirmUnlink(event) {
    const id = event.currentTarget?.dataset?.linkId
    const name = event.currentTarget?.dataset?.providerName || "this integration"
    if (!id) return
    const ok = await confirmAction({
      title: "Unlink integration?",
      message: `Unlink ${name} from this project? Sync activity for this project will stop. The connection itself stays active for other projects.`,
      confirmLabel: "Yes, unlink",
      danger: true
    })
    if (ok) this._doUnlink(id)
  }

  async _doUnlink(id) {
    const url = this.destroyUrlTemplateValue.replace("ID", id)
    try {
      const res = await fetch(url, {
        method: "DELETE",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this._csrfToken()
        },
        credentials: "same-origin"
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok !== false) {
        this._flashAndReload(data.message || "Unlinked.", "notice")
      } else {
        showToast(data.message || "Couldn't unlink. Try again.", "error")
      }
    } catch (e) {
      showToast("Network error — try again.", "error")
    }
  }

  _findPanel({ mode, connectionId, linkId }) {
    return this.formPanelTargets.find((p) => {
      if (p.dataset.formMode !== mode) return false
      if (mode === "create") return p.dataset.connectionId === String(connectionId)
      if (mode === "edit")   return p.dataset.linkId === String(linkId)
      return false
    })
  }

  _panelFromEvent(event) {
    let el = event.target
    while (el) {
      if (el.dataset && el.dataset.projectIntegrationLinksTarget === "formPanel") return el
      el = el.parentElement
    }
    return null
  }

  _flashAndReload(message, type = "notice") {
    try {
      sessionStorage.setItem("__pilFlash", JSON.stringify({ message, type }))
    } catch (e) { /* ignore */ }
    window.location.reload()
  }

  _showPendingFlash() {
    try {
      const raw = sessionStorage.getItem("__pilFlash")
      if (!raw) return
      sessionStorage.removeItem("__pilFlash")
      const parsed = JSON.parse(raw)
      showToast(parsed.message, parsed.type === "alert" ? "error" : "success")
    } catch (e) { /* ignore */ }
  }

  _csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }
}
